/**
 * DashboardPage — consultant-facing client/engagement/run overview
 *
 * Route: /dashboard
 *
 * Data sources:
 *   GET /dashboard/runs  → run list with aggregate scores
 *
 * Clicking a run navigates to /runs/:runId (RunDetailPage) — full page.
 * Analytics has moved to /analytics (AnalyticsPage) — top nav link.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, isClientAdmin, isClutchRole } from '../context/AuthContext'
import { apiFetch } from '../lib/apiClient'
import styles from './DashboardPage.module.css'

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

interface RunRecord {
  run_id:          number
  run_label:       string
  run_date:        string
  engagement_name: string
  client_name:     string
  instrument_code: string
  respondent_count: number
  avg_readiness:   number | null
  avg_resistance:  number | null
  avg_index:       number | null
}

async function fetchRuns(idToken: string | null): Promise<RunRecord[]> {
  return apiFetch<RunRecord[]>('/dashboard/runs', idToken)
}

// ---------------------------------------------------------------------------
// Client/engagement hierarchy types (from /dashboard/clients)
// ---------------------------------------------------------------------------

interface EngagementHierarchyEntry { engagement_id: number; engagement_name: string }
interface ClientHierarchyEntry     { client_id: number; client_name: string; engagements: EngagementHierarchyEntry[] }

async function fetchClientHierarchy(idToken: string | null): Promise<ClientHierarchyEntry[]> {
  return apiFetch<ClientHierarchyEntry[]>('/dashboard/clients', idToken)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Band = 'strong' | 'developing' | 'needs'

function scoreToBand(score: number | null): Band | null {
  if (score === null) return null
  if (score >= 75) return 'strong'
  if (score >= 50) return 'developing'
  return 'needs'
}

function primaryScore(r: RunRecord): number | null {
  return r.instrument_code === 'CODEC-v3' ? r.avg_readiness : r.avg_index
}

function primaryBand(r: RunRecord): Band | null {
  return scoreToBand(primaryScore(r))
}

function avg(nums: number[]): number {
  if (!nums.length) return 0
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)
}

function fmt(n: number | null) { return n !== null ? `${Math.round(n)}%` : '—' }

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function exportCsv(runs: RunRecord[]) {
  const rows = [
    'Run ID,Date,Engagement,Client,Instrument,Run Label,Respondents,Avg Readiness,Avg Resistance,Avg HFA Index',
  ]
  for (const r of runs) {
    rows.push(
      [
        r.run_id, r.run_date, `"${r.engagement_name}"`, `"${r.client_name}"`,
        r.instrument_code, r.run_label, r.respondent_count,
        r.avg_readiness ?? '', r.avg_resistance ?? '', r.avg_index ?? '',
      ].join(','),
    )
  }
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = `clutch-ocm-runs-${new Date().toISOString().slice(0, 10)}.csv`
  a.click(); URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const BAND_LABELS: Record<Band, string> = {
  strong:     'Strong',
  developing: 'Developing',
  needs:      'Needs Support',
}

function bandClass(band: Band) {
  return band === 'strong'     ? styles.bandStrong
    : band === 'developing'    ? styles.bandDeveloping
    : styles.bandNeeds
}

function StatCard({ label, value, sub, accent, score, invert = false }: {
  label: string; value: string | number; sub?: string
  accent?: 'aqua' | 'magenta' | 'blue' | 'gold'
  score?: number | null; invert?: boolean
}) {
  // If score is provided, derive accent from band instead of hardcoded color
  let accentClass = accent ? styles[`accent_${accent}`] : ''
  if (score !== null && score !== undefined) {
    const effective = invert ? 100 - score : score
    const band = effective >= 75 ? 'strong' : effective >= 50 ? 'developing' : 'needs'
    accentClass = styles[`accent_${band}`]
  }
  return (
    <div className={`${styles.statCard} ${accentClass}`}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  )
}

function ScoreBar({ score }: { score: number }) {
  const bandColor = score >= 75 ? 'var(--strong)' : score >= 50 ? 'var(--developing)' : 'var(--needs)'
  return (
    <div className={styles.scoreBarWrap}>
      <div
        className={styles.scoreBarFill}
        style={{ width: `${Math.min(score, 100)}%`, background: bandColor }}
      />
    </div>
  )
}

function BandPill({ band }: { band: Band }) {
  return (
    <span className={`${styles.bandPill} ${bandClass(band)}`}>{BAND_LABELS[band]}</span>
  )
}

function InstrumentTag({ code }: { code: string }) {
  const codec = code === 'CODEC-v3'
  return (
    <span className={`${styles.instrTag} ${codec ? styles.instrCodec : styles.instrHfa}`}>
      {codec ? 'CODE-C' : 'HFA'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Org hierarchy helpers
// ---------------------------------------------------------------------------

interface EngGroup    { engagement_name: string; runs: RunRecord[] }
interface ClientGroup { client_name: string; engagements: EngGroup[] }

/**
 * Merge the full client/engagement hierarchy (including zero-run entries)
 * with the current filtered run list.
 *
 * When filters are active we fall back to run-derived grouping so the list
 * narrows correctly. When no filter is set the hierarchy is the authoritative
 * source of structure so empty clients/engagements appear.
 */
function buildGrouped(
  hierarchy: ClientHierarchyEntry[],
  filteredRuns: RunRecord[],
  hasFilters: boolean,
): ClientGroup[] {
  // Build lookup: "client||engagement" → matching filtered runs
  const runsByKey = new Map<string, RunRecord[]>()
  for (const run of filteredRuns) {
    const key = `${run.client_name}||${run.engagement_name}`
    if (!runsByKey.has(key)) runsByKey.set(key, [])
    runsByKey.get(key)!.push(run)
  }

  if (hasFilters) {
    // Filter mode: only show entries that have at least one matching run
    return hierarchy
      .map(client => ({
        client_name: client.client_name,
        engagements: client.engagements
          .map(eng => ({
            engagement_name: eng.engagement_name,
            runs: runsByKey.get(`${client.client_name}||${eng.engagement_name}`) ?? [],
          }))
          .filter(eng => eng.runs.length > 0),
      }))
      .filter(client => client.engagements.length > 0)
  }

  // No filter: use hierarchy as structure, overlay runs
  return hierarchy.map(client => ({
    client_name: client.client_name,
    engagements: client.engagements.map(eng => ({
      engagement_name: eng.engagement_name,
      runs: runsByKey.get(`${client.client_name}||${eng.engagement_name}`) ?? [],
    })),
  }))
}

function EngagementGroup({ eng, onSelect, onOutreach }: {
  eng:          EngGroup
  onSelect:     (r: RunRecord) => void
  onOutreach?:  (r: RunRecord) => void
}) {
  // Default CLOSED — consultants click in to expand
  const [open, setOpen] = React.useState(false)
  const totalRespondents = eng.runs.reduce((s, r) => s + r.respondent_count, 0)
  const instruments = [...new Set(eng.runs.map(r => r.instrument_code))]
  const hasRuns = eng.runs.length > 0

  return (
    <div className={styles.orgEngagement}>
      <button
        className={styles.orgEngToggle}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`${styles.orgEngChevron} ${open ? styles.orgEngChevronOpen : styles.orgEngChevronClosed}`}>▼</span>
        <span className={styles.orgEngName}>{eng.engagement_name}</span>
        <span className={styles.orgEngMeta}>
          {instruments.map(code => <InstrumentTag key={code} code={code} />)}
          <span>{eng.runs.length} run{eng.runs.length !== 1 ? 's' : ''}</span>
          {hasRuns && <span>{totalRespondents} respondent{totalRespondents !== 1 ? 's' : ''}</span>}
        </span>
      </button>

      {open && (
        <div className={styles.orgEngBody}>
          {!hasRuns ? (
            <div className={styles.orgNoRuns}>No assessment runs yet</div>
          ) : (
            <>
          <div className={`${styles.orgRunHeader} ${onOutreach ? styles.orgRunHeaderWithOutreach : ''}`}>
            <span className={styles.orgRunHeaderLabel}>Date</span>
            <span className={styles.orgRunHeaderLabel}>Label</span>
            <span className={styles.orgRunHeaderLabel}>Instrument</span>
            <span className={styles.orgRunHeaderLabel}>Respondents</span>
            <span className={styles.orgRunHeaderLabel}>Primary Score</span>
            <span className={styles.orgRunHeaderLabel}>Band</span>
            <span />
          </div>
          {eng.runs.map(r => {
            const ps   = primaryScore(r)
            const band = primaryBand(r)
            return (
              <div
                key={r.run_id}
                className={`${styles.orgRunRow} ${onOutreach ? styles.orgRunRowWithOutreach : ''}`}
                onClick={() => onSelect(r)}
              >
                <span className={styles.orgRunDate}>{formatDate(r.run_date)}</span>
                <span className={styles.orgRunLabel}>{r.run_label}</span>
                <InstrumentTag code={r.instrument_code} />
                <span className={styles.orgRunRespond}>{r.respondent_count}</span>
                <div>
                  {ps !== null ? (
                    <div className={styles.scoreCell}>
                      <ScoreBar score={ps} />
                      <span className={styles.scorePct}>{Math.round(ps)}%</span>
                    </div>
                  ) : <span className={styles.noScore}>—</span>}
                </div>
                <div>
                  {band ? <BandPill band={band} /> : <span className={styles.noScore}>—</span>}
                </div>
                {onOutreach && (
                  <button
                    className={styles.outreachBtn}
                    onClick={e => { e.stopPropagation(); onOutreach(r) }}
                    title="Go to Outreach"
                  >
                    Outreach
                  </button>
                )}
                <button
                  className={styles.viewBtn}
                  onClick={e => { e.stopPropagation(); onSelect(r) }}
                >
                  →
                </button>
              </div>
            )
          })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ClientGroupRow({ client, onSelect, onOutreach }: {
  client:       ClientGroup
  onSelect:     (r: RunRecord) => void
  onOutreach?:  (r: RunRecord) => void
}) {
  // Default CLOSED — opening page shows clean collapsed client list
  const [open, setOpen] = React.useState(false)
  const totalRuns = client.engagements.reduce((s, e) => s + e.runs.length, 0)
  const totalRespondents = client.engagements.reduce(
    (s, e) => s + e.runs.reduce((sr, r) => sr + r.respondent_count, 0), 0,
  )

  return (
    <div className={styles.orgClient}>
      <button
        className={styles.orgClientToggle}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`${styles.orgClientChevron} ${open ? styles.orgClientChevronOpen : styles.orgClientChevronClosed}`}>▼</span>
        <span className={styles.orgClientName}>{client.client_name}</span>
        <span className={styles.orgClientMeta}>
          <span>{client.engagements.length} engagement{client.engagements.length !== 1 ? 's' : ''}</span>
          <span>{totalRuns} run{totalRuns !== 1 ? 's' : ''}</span>
          <span>{totalRespondents} respondent{totalRespondents !== 1 ? 's' : ''}</span>
        </span>
      </button>

      {open && (
        <div className={styles.orgClientBody}>
          {client.engagements.map(eng => (
            <EngagementGroup
              key={eng.engagement_name}
              eng={eng}
              onSelect={onSelect}
              onOutreach={onOutreach}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading / Error states
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className={styles.stateScreen}>
      <div className={styles.spinner} />
      <p>Loading dashboard data…</p>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className={styles.stateScreen}>
      <p className={styles.errorMsg}>Failed to load: {message}</p>
      <button className={styles.retryBtn} onClick={onRetry}>Retry</button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type FilterInstrument = 'all' | 'codec' | 'hfa'
type FilterBand       = 'all' | Band

export function DashboardPage() {
  const navigate = useNavigate()
  const { idToken, platformUser } = useAuth()
  const clientAdmin = isClientAdmin(platformUser)

  const [runs,      setRuns]      = useState<RunRecord[]>([])
  const [hierarchy, setHierarchy] = useState<ClientHierarchyEntry[]>([])
  const [loading,   setLoading]   = useState(true)
  const [apiError,  setApiError]  = useState('')

  // Persist filters in sessionStorage so navigating to Analytics and back restores them (RP-004)
  const [instrFilter, setInstrFilter] = useState<FilterInstrument>(
    () => (sessionStorage.getItem('dash_instrFilter') as FilterInstrument) || 'all',
  )
  const [bandFilter, setBandFilter] = useState<FilterBand>(
    () => (sessionStorage.getItem('dash_bandFilter') as FilterBand) || 'all',
  )
  const [teamSearch, setTeamSearch] = useState(
    () => sessionStorage.getItem('dash_teamSearch') || '',
  )
  const [sortField] = useState<'date' | 'score' | 'team'>('date')
  const [sortDir]   = useState<'asc' | 'desc'>('desc')

  const load = useCallback(() => {
    setLoading(true); setApiError('')
    Promise.allSettled([fetchRuns(idToken), fetchClientHierarchy(idToken)])
      .then(([runsResult, hierarchyResult]) => {
        if (runsResult.status === 'fulfilled') setRuns(runsResult.value)
        else setApiError(runsResult.reason?.message ?? 'Failed to load runs')
        if (hierarchyResult.status === 'fulfilled') setHierarchy(hierarchyResult.value)
        // hierarchy failure is non-fatal: dashboard still works, just won't show empty clients
        setLoading(false)
      })
  }, [idToken])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    let list = runs.filter(r => {
      if (instrFilter === 'codec' && r.instrument_code !== 'CODEC-v3') return false
      if (instrFilter === 'hfa'   && r.instrument_code !== 'HFA-v3')   return false
      const band = primaryBand(r)
      if (bandFilter !== 'all' && band !== bandFilter) return false
      if (teamSearch) {
        const q = teamSearch.toLowerCase()
        if (!r.engagement_name.toLowerCase().includes(q) &&
            !r.client_name.toLowerCase().includes(q)) return false
      }
      return true
    })
    list = [...list].sort((a, b) => {
      let cmp = 0
      if (sortField === 'date')  cmp = a.run_date.localeCompare(b.run_date)
      if (sortField === 'team')  cmp = a.engagement_name.localeCompare(b.engagement_name)
      if (sortField === 'score') {
        const sa = primaryScore(a) ?? -1
        const sb = primaryScore(b) ?? -1
        cmp = sa - sb
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
    return list
  }, [runs, instrFilter, bandFilter, teamSearch, sortField, sortDir])

  const stats = useMemo(() => {
    const codecRuns = runs.filter(r => r.instrument_code === 'CODEC-v3')
    const hfaRuns   = runs.filter(r => r.instrument_code === 'HFA-v3')
    const readinessNums  = codecRuns.map(r => r.avg_readiness).filter((n): n is number => n !== null)
    const resistanceNums = codecRuns.map(r => r.avg_resistance).filter((n): n is number => n !== null)
    const indexNums      = hfaRuns.map(r => r.avg_index).filter((n): n is number => n !== null)
    const bandCounts = { strong: 0, developing: 0, needs: 0 }
    runs.forEach(r => { const b = primaryBand(r); if (b) bandCounts[b]++ })
    return {
      totalRuns:    runs.length,
      codecRuns:    codecRuns.length,
      hfaRuns:      hfaRuns.length,
      totalRespondents: runs.reduce((s, r) => s + r.respondent_count, 0),
      avgReadiness:  readinessNums.length  ? avg(readinessNums)  : null,
      avgResistance: resistanceNums.length ? avg(resistanceNums) : null,
      avgIndex:      indexNums.length      ? avg(indexNums)      : null,
      bandCounts,
    }
  }, [runs])

  const hasFilters = instrFilter !== 'all' || bandFilter !== 'all' || teamSearch !== ''
  const grouped = useMemo(
    () => buildGrouped(hierarchy, filtered, hasFilters),
    [hierarchy, filtered, hasFilters],
  )

  if (loading) return <LoadingState />
  if (apiError) return <ErrorState message={apiError} onRetry={load} />

  const clutchRole = isClutchRole(platformUser)

  // Navigate to full-page run detail on row click
  function handleSelectRun(r: RunRecord) {
    navigate(`/runs/${r.run_id}`)
  }

  // Navigate directly to Outreach tab (clutch staff only)
  function handleOutreachRun(r: RunRecord) {
    navigate(`/runs/${r.run_id}?tab=outreach`)
  }

  return (
    <div className={styles.page}>

      {/* Page header */}
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>{clientAdmin ? 'Client Dashboard' : 'Consultant Dashboard'}</div>
          <h1 className={styles.pageTitle}>Clients</h1>
          <p className={styles.pageSub}>
            {stats.totalRuns} runs · {stats.totalRespondents} total respondents
          </p>
        </div>
        <button className={styles.exportBtn} onClick={() => exportCsv(filtered)}>
          ↓ Export CSV
          {filtered.length < runs.length && (
            <span className={styles.exportCount}> ({filtered.length})</span>
          )}
        </button>
      </div>

      {/* Summary cards */}
      <div className={styles.statsGrid}>
        <StatCard
          label="Assessment Runs"
          value={stats.totalRuns}
          sub={`${stats.codecRuns} CODE-C · ${stats.hfaRuns} HFA`}
        />
        <StatCard
          label="Avg CODE-C Readiness"
          value={stats.avgReadiness !== null ? fmt(stats.avgReadiness) : '—'}
          score={stats.avgReadiness}
        />
        <StatCard
          label="Avg CODE-C Resistance"
          value={stats.avgResistance !== null ? fmt(stats.avgResistance) : '—'}
          score={stats.avgResistance}
          invert
        />
        <StatCard
          label="Avg HFA Index"
          value={stats.avgIndex !== null ? fmt(stats.avgIndex) : '—'}
          score={stats.avgIndex}
        />
        {stats.totalRuns > 0 && (
          <div className={`${styles.statCard} ${styles.bandCard}`}>
            <div className={styles.statLabel}>Score Distribution</div>
            <div className={styles.bandDistRow}>
              {(['strong', 'developing', 'needs'] as Band[]).map(b => (
                <div key={b} className={styles.bandDistItem}>
                  <div
                    className={styles.bandDistBar}
                    style={{ '--pct': `${(stats.bandCounts[b] / stats.totalRuns) * 100}%` } as React.CSSProperties}
                    data-color={b}
                  />
                  <span className={styles.bandDistLabel}>{BAND_LABELS[b]}</span>
                  <span className={styles.bandDistVal}>{stats.bandCounts[b]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Instrument</label>
          <div className={styles.pillGroup}>
            {(['all', 'codec', 'hfa'] as FilterInstrument[]).map(v => (
              <button
                key={v}
                className={`${styles.filterPill} ${instrFilter === v ? styles.filterPillActive : ''}`}
                onClick={() => { setInstrFilter(v); sessionStorage.setItem('dash_instrFilter', v) }}
              >
                {v === 'all' ? 'All' : v === 'codec' ? 'CODE-C' : 'HFA'}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Score band</label>
          <div className={styles.pillGroup}>
            {(['all', 'strong', 'developing', 'needs'] as FilterBand[]).map(v => (
              <button
                key={v}
                className={`${styles.filterPill} ${bandFilter === v ? styles.filterPillActive : ''}`}
                onClick={() => { setBandFilter(v); sessionStorage.setItem('dash_bandFilter', v) }}
              >
                {v === 'all' ? 'All' : BAND_LABELS[v as Band]}
              </button>
            ))}
          </div>
        </div>
        <div className={`${styles.filterGroup} ${styles.filterSearch}`}>
          <label className={styles.filterLabel}>Search</label>
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Filter by engagement or client…"
            value={teamSearch}
            onChange={e => { setTeamSearch(e.target.value); sessionStorage.setItem('dash_teamSearch', e.target.value) }}
          />
        </div>
        {hasFilters && (
          <button
            className={styles.clearBtn}
            onClick={() => {
              setInstrFilter('all'); setBandFilter('all'); setTeamSearch('')
              sessionStorage.removeItem('dash_instrFilter')
              sessionStorage.removeItem('dash_bandFilter')
              sessionStorage.removeItem('dash_teamSearch')
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div className={styles.resultsCount}>
        Showing <strong>{filtered.length}</strong> of {runs.length} runs
      </div>

      {/* Org hierarchy — collapsed by default */}
      {filtered.length === 0 ? (
        <div className={styles.orgEmptyNote}>
          {runs.length === 0
            ? 'No assessment runs found. Seed data or create runs to get started.'
            : 'No runs match the current filters.'}
        </div>
      ) : (
        <div className={styles.orgTable}>
          {grouped.map(client => (
            <ClientGroupRow
              key={client.client_name}
              client={client}
              onSelect={handleSelectRun}
              onOutreach={clutchRole ? handleOutreachRun : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}
